import { Controller } from "@hotwired/stimulus";

// HTMX-specific version of filterable_form.js for unified search filters
export default class extends Controller {
  static targets = ['form']
  static values = {
    excludeFromUrl: Array,
    baseUrl: String
  }

  connect() {
    if (!this.hasFormTarget) {
      const f = document.getElementById('unified-search-form')
      if (f) { this.formTarget = f }
    }

    this.formTarget.addEventListener('htmx:afterRequest', (e) => {
      if (!e.detail?.successful) return
      if (this.isPaginationRequest(e)) return
      this.updateURLFromForm()
    })

    this.handleBeforeRequest = this.handleBeforeRequest.bind(this)
    document.addEventListener('htmx:beforeRequest', this.handleBeforeRequest)
  }

  handleBeforeRequest(event) {
    const params = event.detail?.requestConfig?.parameters
    const elt = event.detail?.elt

    this.handleSupplierRemovals(elt)
    this.handleProductRemovals(elt)
    this.handleSearchTypeChange(params)
  }

  handleSupplierRemovals(elt) {
    const ds = elt?.dataset
    if (!ds) return
    if (ds.filterCondition) this.removeConditionHiddenFields(ds.filterCondition)
    if ('clearAllFilters' in ds) this.removeAllConditionHiddenFields()
  }

  handleProductRemovals(elt) {
    const ds = elt?.dataset
    if (!ds) return
    if (ds.productFilterKey) this.removeProductKeyHiddenFields(ds.productFilterKey)
    if ('productClearAll' in ds) this.removeAllProductFilterHiddenFields()
  }

  handleSearchTypeChange(params) {
    const next = params?.search_type
    const current = params?.current_search_type
    if (!next) return

    if (current && next !== current) {
      this.removeAllConditionHiddenFields()
      this.removeAllProductFilterHiddenFields()
    }

    this.ensureHiddenField('search_type', next)
  }
  
  // ---------- Filtering / Hidden field helpers ----------
  
  removeConditionHiddenFields(condition) {
    if (!condition) return
    const prefix = `filters[conditions][${condition}]`
    this.formTarget.querySelectorAll(`input[name^="${prefix}"]`).forEach(el => el.remove())
  }

  removeAllConditionHiddenFields() {
    this.formTarget.querySelectorAll('input[name^="filters[conditions]"]').forEach(el => el.remove())
  }

  purgeOrphanValueInputs() {
    const valueInputs = this.formTarget.querySelectorAll('input[name^="filters[conditions]"][name*="[value]"]')
    valueInputs.forEach(input => {
      const aggs = this.parseAggsName(input.name)
      if (!aggs) return
      const keyField = this.formTarget.querySelector(`input[name="filters[conditions][${aggs}][key]"]`)
      const compField = this.formTarget.querySelector(`input[name="filters[conditions][${aggs}][comparator]"]`)
      if (!keyField || !compField) {
        input.remove()
      }
    })
  }
  
  presentValue(v) {
    if (v === undefined || v === null) return false
    const s = String(v).trim()
    return s.length > 0 && s !== 'undefined'
  }

  ensureHiddenField(name, value) {
    if (!this.presentValue(value)) return null
    let el = this.formTarget.querySelector(`[name="${name}"]`)
    if (!el) {
      el = document.createElement('input')
      el.type = 'hidden'
      el.name = name
      this.formTarget.appendChild(el)
    }
    el.value = value
    return el
  }

  removeHiddenFieldsByName(name) {
    this.formTarget.querySelectorAll(`[name="${name}"]`).forEach(el => el.remove())
  }

  removeInputs(name) {
    this.formTarget.querySelectorAll(`[name="${name}"]`).forEach(el => el.remove())
  }

  buildInput(name, value) {
    if (!this.presentValue(value)) return null
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    return input
  }

  parseAggsName(paramName) {
    const m = paramName.match(/\[conditions]\[([^\]]+)]\[value]/)
    return m ? m[1] : null
  }

  syncFacetKeyComparator(aggsName, selectedOptions) {
    if (!aggsName) return
    const anySelected = (selectedOptions || []).some(v => this.presentValue(v))

    const keyName        = `filters[conditions][${aggsName}][key]`
    const comparatorName = `filters[conditions][${aggsName}][comparator]`

    if (anySelected) {
      this.ensureHiddenField(keyName, aggsName)
      this.ensureHiddenField(comparatorName, 'any')
    } else {
      this.removeHiddenFieldsByName(keyName)
      this.removeHiddenFieldsByName(comparatorName)
    }

    // If facet has static hidden inputs in markup, toggle disabled
    const domKey = document.getElementById(`${aggsName}_hidden_key`)
    const domComp = document.getElementById(`${aggsName}_hidden_comparator`)
    if (domKey)  domKey.disabled  = !anySelected
    if (domComp) domComp.disabled = !anySelected
  }

  prepareFormForSubmit() {
    this.ensureHiddenField('page', '1')
    const sidebar = document.getElementById('ai-search-filters')
    if (sidebar && sidebar.dataset.searchType) {
      this.ensureHiddenField('search_type', sidebar.dataset.searchType)
    }
  }

  // ---------- Event from checkbox reporter ----------

  handleNewFilterOptions(event) {
    let name = event.detail.name
    let options = event.detail.options
    let value = event.detail.value

    if (options !== undefined && Array.isArray(options) && typeof name === 'string') {
      this.removeInputs(name)
      const cleaned = options.filter(v => this.presentValue(v))
      let inputs = cleaned.map(val => this.buildInput(name, val)).filter(Boolean)
      this.formTarget.append(...inputs)

      const aggsName = this.parseAggsName(name)
      this.syncFacetKeyComparator(aggsName, cleaned)

      this.dispatch('fields-updated', { detail: event.detail })
      // For HTMX: trigger form submission manually
      if (this.formTarget.dataset.hxGet) {
        this.prepareFormForSubmit()
        htmx.trigger(this.formTarget, 'submit')
      }
    } else if (value !== undefined && typeof name === 'string') {
      this.removeInputs(name)
      if (this.presentValue(value)) {
        let input = this.buildInput(name, value)
        if (input) this.formTarget.append(input)
        const aggsName = this.parseAggsName(name)
        this.syncFacetKeyComparator(aggsName, [value])
      } else {
        const aggsName = this.parseAggsName(name)
        this.syncFacetKeyComparator(aggsName, [])
      }

      this.dispatch('fields-updated', { detail: event.detail })
      // For HTMX: trigger form submission manually
      if (this.formTarget.dataset.hxGet) {
        this.prepareFormForSubmit()
        htmx.trigger(this.formTarget, 'submit')
      }
    }
  }

  updateURLFromForm() {
    this.purgeOrphanValueInputs()

    const formData = new FormData(this.formTarget)
    const exclude = this.exclusionSet()
    const params = new URLSearchParams()


    const baseUrl = this.hasBaseUrlValue ? this.baseUrlValue : '/search'
    if (baseUrl === '/search') {
      const searchType = formData.get('search_type') || 'all'
      params.set('search_type', searchType)
    }

    const multiMap = new Map()

    for (const [key, rawVal] of formData.entries()) {
      this.processFormEntry(key, rawVal, params, multiMap, exclude)
    }

    multiMap.forEach((set, key) => {
      set.forEach(v => params.append(key, v))
    })

    const qs = params.toString()
    const newURL = qs ? `${baseUrl}?${qs}` : baseUrl   
    window.history.pushState({}, '', newURL)
  }

  processFormEntry(key, rawVal, params, multiMap, exclude) {
    if (key === 'search_type') return
    if (exclude.has(key)) return
    if (rawVal === null || rawVal === undefined) return
    const value = String(rawVal).trim()
    if (!this.presentValue(value)) return

    if (this.isMultiValueKey(key)) {
      if (!multiMap.has(key)) multiMap.set(key, new Set())
      multiMap.get(key).add(value)
      return
    }

    if (!params.has(key)) {
      params.append(key, value)
      return
    }

    if (params.get(key) === value) return

    // promote to multi
    const existing = params.getAll(key)
    params.delete(key)
    const multiKey = `${key}[]`
    if (!multiMap.has(multiKey)) multiMap.set(multiKey, new Set(existing))
    multiMap.get(multiKey).add(value)
  }

  isMultiValueKey(key) {
    return /\[\]$/.test(key)
  }

  exclusionSet() {
    const base = new Set()
    if (this.hasExcludeFromUrlValue && Array.isArray(this.excludeFromUrlValue)) {
      this.excludeFromUrlValue.forEach(k => {
        if (k && typeof k === 'string') base.add(k)
      })
    }
    return base
  }

  isPaginationRequest(e) {
    const p = e.detail?.requestConfig?.parameters
    return p && p.page && p.page !== '1'
  }

  currentSearchType() {
    const el = this.formTarget.querySelector('input[name="search_type"]')
    return el ? el.value : 'all'
  }

  removeProductKeyHiddenFields(key) {
    if (!key) return
    this.formTarget
      .querySelectorAll(`input[name^="filters[${key}]"]`)
      .forEach(el => el.remove())
  }

  removeAllProductFilterHiddenFields() {
    this.formTarget
      .querySelectorAll('input[name^="filters["]:not([name^="filters[conditions]"])')
      .forEach(el => el.remove())
  }
}